import {
  employeeApi,
  roleApi,
  locationApi,
  positionApi,
  payrollDepartmentApi,
  scheduleApi,
  paymentTypeApi,
  paymentFrequencyApi,
  payRecordApi,
} from '../services/api';
import type {
  Employee,
  Location,
  PayRecord,
  PaymentFrequency,
  PaymentType,
  PayrollDepartment,
  Position,
  Role,
  Schedule,
} from '../types';

export type PayrollReportReferenceData = {
  roles: Role[];
  locations: Location[];
  positions: Position[];
  payrollDepartments: PayrollDepartment[];
  schedules: Schedule[];
  paymentTypes: PaymentType[];
  paymentFrequencies: PaymentFrequency[];
};

export type EmployeeLookupMaps = {
  worksNumberByEmployeeId: Record<number, string>;
  firstNameByEmployeeId: Record<number, string>;
  lastNameByEmployeeId: Record<number, string>;
};

let referenceCache: PayrollReportReferenceData | null = null;
let referenceInflight: Promise<PayrollReportReferenceData> | null = null;

let employeesCache: Employee[] | null = null;
let employeesInflight: Promise<Employee[]> | null = null;

let payRecordsCache: PayRecord[] | null = null;
let payRecordsInflight: Promise<PayRecord[]> | null = null;

/** Small reference lists for filter dropdowns and period pickers (deduped across payroll report pages). */
export async function loadPayrollReportReferenceData(): Promise<PayrollReportReferenceData> {
  if (referenceCache) return referenceCache;
  if (!referenceInflight) {
    referenceInflight = Promise.all([
      roleApi.getAll(),
      locationApi.getAll(),
      positionApi.getAll(),
      payrollDepartmentApi.getAll(),
      scheduleApi.getAll(),
      paymentTypeApi.getAll(),
      paymentFrequencyApi.getAll(),
    ])
      .then(
        ([
          roles,
          locations,
          positions,
          payrollDepartments,
          schedules,
          paymentTypes,
          paymentFrequencies,
        ]) => {
          const data: PayrollReportReferenceData = {
            roles: roles || [],
            locations: (locations || []).filter((l) => l.isActive),
            positions: (positions || []).filter((p) => p.isActive),
            payrollDepartments: (payrollDepartments || []).filter((d) => d.isActive),
            schedules: (schedules || []).filter((s) => s.isActive),
            paymentTypes: paymentTypes || [],
            paymentFrequencies: paymentFrequencies || [],
          };
          referenceCache = data;
          return data;
        }
      )
      .finally(() => {
        referenceInflight = null;
      });
  }
  return referenceInflight;
}

/** Employee list for filters and name columns (no presigned profile images). */
export async function loadPayrollReportEmployees(): Promise<Employee[]> {
  if (employeesCache && employeesCache.length > 0) return employeesCache;
  if (!employeesInflight) {
    employeesInflight = employeeApi
      .getAll({ includePayroll: true, presignImages: false })
      .then((data) => {
        employeesCache = data || [];
        return employeesCache;
      })
      .finally(() => {
        employeesInflight = null;
      });
  }
  return employeesInflight;
}

/** Pay records — only needed when pay-record filters are used or legacy payroll runs UI. */
export async function loadPayrollReportPayRecords(): Promise<PayRecord[]> {
  if (payRecordsCache) return payRecordsCache;
  if (!payRecordsInflight) {
    payRecordsInflight = payRecordApi
      .getAll()
      .then((data) => {
        payRecordsCache = data || [];
        return payRecordsCache;
      })
      .finally(() => {
        payRecordsInflight = null;
      });
  }
  return payRecordsInflight;
}

export function buildEmployeeLookupMaps(employees: Employee[]): EmployeeLookupMaps {
  const worksNumberByEmployeeId: Record<number, string> = {};
  const firstNameByEmployeeId: Record<number, string> = {};
  const lastNameByEmployeeId: Record<number, string> = {};
  for (const e of employees) {
    firstNameByEmployeeId[e.id] = e.firstName ?? '';
    lastNameByEmployeeId[e.id] = e.lastName ?? '';
    const wn = e.payroll?.worksNumber;
    if (wn) worksNumberByEmployeeId[e.id] = wn;
  }
  return { worksNumberByEmployeeId, firstNameByEmployeeId, lastNameByEmployeeId };
}

export function filtersUsePayRecords(filters: Array<{ field?: string; value?: unknown; valueFrom?: unknown; valueTo?: unknown }>): boolean {
  return filters.some((f) => {
    const field = String(f.field || '').toLowerCase();
    if (!field.startsWith('payrecord')) return false;
    return !!(f.value || f.valueFrom || f.valueTo);
  });
}
